import React, { useState } from 'react';
import {
  Box,
  Text,
  Flex,
  Avatar,
  IconButton,
  Collapse,
  Tooltip,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

const BookingCard = ({ booking, onClick }) => {
  const [isOpen, setIsOpen] = useState(false);

  const formatTime = (time) => {
    const [hour, minute] = time.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  const formattedStartTime = formatTime(booking.start_time);
  const filteredItems = booking.items.filter((item) => !item.is_current_user);
  const groupTitle =
    filteredItems.length > 1
      ? 'Group Booking'
      : `${filteredItems[0]?.first_name} ${filteredItems[0]?.last_name}`;

  return (
    <Box
      bg="gray.900"
      color="white"
      p={4}
      borderRadius="lg"
      shadow="md"
      mb={4}
      position="relative"
      _hover={{ transform: 'scale(1.02)', transition: '0.2s ease' }}
      cursor="pointer"
      onClick={() => onClick && onClick(booking)}
    >
      {/* Dropdown button */}
      <IconButton
        icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
        size="sm"
        variant="ghost"
        position="absolute"
        top="8px"
        right="8px"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        aria-label="Toggle Booking Details"
      />

      {/* Booking Summary */}
      <Flex justify="space-between" align="center">
        <Box pr={6}>
          <Text fontSize="lg" fontWeight="bold">
            {groupTitle}
          </Text>
          <Text fontSize="sm" color="gray.400">
            {booking.from_date} @ {formattedStartTime}
          </Text>
          <Text fontSize="xs" color="gray.500" mt={1}>
            Status: {booking.status}
          </Text>
          {/* Payment info - customer sees total, others see their payout */}
					{(() => {
						const current = booking.items.find(i => i.is_current_user);

						if (!current) return null;

						if (current.user_type === 'customer') {
							return (
								<Text fontSize="sm" color="green.300" fontWeight="bold" mt={1}>
									Total: ${booking.total.toFixed(2) / 100}
								</Text>
							);
						} else {
							return (
								<Text fontSize="sm" color="blue.300" fontWeight="bold" mt={1}>
									Your Payout: ${ (current.payout / 100).toFixed(2) }
								</Text>
							);
						}
					})()}

        </Box>

        {/* Avatar group */}
        <Flex align="center" gap={2}>
          {filteredItems.slice(0, 3).map((item, index) => (
            <Tooltip key={index} label={`${item.first_name} ${item.last_name}`}>
              <a
                href={`/user/${item.user_id}`}
                onClick={(e) => {
                  e.preventDefault();
                  window.open(`/user/${item.user_id}`, '_blank');
                }}
              >
                <Avatar
                  size="md"
                  name={`${item.first_name} ${item.last_name}`}
                  src={item.avatar}
                />
              </a>
            </Tooltip>
          ))}
          {filteredItems.length > 3 && (
            <Text fontSize="sm" color="gray.400">
              +{filteredItems.length - 3} more
            </Text>
          )}
        </Flex>
      </Flex>

      {/* Drop Down Detail */}
      <Collapse in={isOpen} animateOpacity>
        <Box mt={4} p={3} bg="gray.800" borderRadius="md">
          {booking.description && (
            <Text fontSize="sm" color="gray.300" mb={3}>
              <strong>Description:</strong> {booking.description}
            </Text>
          )}

          <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.100">
            Participants:
          </Text>

          {[...filteredItems]
            .sort((a, b) => (a.user_type === 'customer' ? -1 : 1))
            .map((item, idx) => (
              <Flex
                key={idx}
                align="center"
                gap={3}
                mb={2}
                p={2}
                bg="gray.700"
                borderRadius="md"
                _hover={{ bg: 'gray.600' }}
              >
                <Avatar
                  size="sm"
                  name={`${item.first_name} ${item.last_name}`}
                  src={item.avatar}
                />
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="white">
                    {item.first_name} {item.last_name}
                  </Text>
                  <Text fontSize="xs" color="gray.300">
                    {item.user_type?.charAt(0).toUpperCase() + item.user_type?.slice(1)}
                  </Text>
                  {booking.items.find((i) => i.is_current_user && i.user_type === 'customer') && (
                    <Text fontSize="xs" color="gray.400">
                      Rate: ${(item.rate_per_hour / 100).toFixed(2)}/hr
                    </Text>
                  )}
                </Box>
              </Flex>
            ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default BookingCard;
